import moment from 'moment';
import { useEffect, useState } from 'react';

import { useQueryParameters } from '@treams/treams-hooks';

import { AnalyticsFilters, LookupFilters } from './types';

export const FILTER_KEY = 'analytics-filters';

// Scoped per user so accounts sharing a device keep their own date range and
// employment filter. Persistence is skipped until the user is known.
export default function useFilters(userID?: string): AnalyticsFilters {
	const storageKey = userID ? `${FILTER_KEY}:${userID}` : '';

	const [userFilter, setUserFilter] = useState<'active' | 'all'>('active');
	const [endDate, setEndDate] = useState<Date>(moment().endOf('year').toDate());
	const [startDate, setStartDate] = useState<Date>(moment().startOf('year').toDate());
	const [timeInterval, setTimeInterval] = useState<string>('year');

	const { interval, cursor, end, users, synced, template, request } = useQueryParameters([
		'end',
		'users',
		'synced',
		'cursor',
		'interval',
		'template',
		'request',
	]);

	useEffect(() => {
		if (!storageKey) return;

		const stored = localStorage.getItem(storageKey);
		const { users, start, end, interval } = JSON.parse(stored || '{}');

		if (users) setUserFilter(users);
		if (end) setEndDate(moment(end).toDate());
		if (start) setStartDate(moment(start).toDate());
		if (interval) setTimeInterval(interval);
	}, [storageKey]);

	useEffect(() => {
		if (!users) return;

		setUserFilter(users === 'all' ? 'all' : 'active');
	}, [users]);

	useEffect(() => {
		if (!cursor || !interval || !end) return;

		const { startDate, endDate } = parseDateRange({ cursor, interval, end });

		setEndDate(endDate);
		setStartDate(startDate);
		setTimeInterval(interval);
	}, [interval, cursor, end]);

	useEffect(() => {
		if (!storageKey) return;

		localStorage.setItem(
			storageKey,
			JSON.stringify({
				start: startDate?.toISOString() ?? null,
				end: endDate,
				users: userFilter,
				interval: timeInterval,
			}),
		);
	}, [storageKey, startDate, endDate, userFilter, timeInterval]);

	return {
		synced,
		endDate,
		startDate,
		userFilter,
		timeInterval,
		engagementTemplateID: template || undefined,
		engagementRequestID: request || undefined,
	};
}

function parseDateRange(filters: LookupFilters): { startDate: Date; endDate: Date } {
	const cursor = filters.cursor;
	const interval = filters.interval;
	const end = filters.end;
	const unit = (interval ?? 'year') as moment.unitOfTime.StartOf;
	const startDate = cursor
		? moment(cursor).startOf(unit).toDate()
		: moment().startOf('year').toDate();
	const endDate = end ? moment(end).endOf('day').toDate() : moment(startDate).endOf(unit).toDate();

	return { startDate, endDate };
}
